import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import useMobile from '../hooks/useMobile';

export default function BottomNav() {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, logout } = useAuth();
  const { T } = useTheme();
  const isMobile = useMobile();
  const [moreOpen, setMoreOpen] = useState(false);

  if (!isMobile) return null;

  const path = location.pathname;

  const isActive = (key) => {
    if (key === 'more') return moreOpen;
    if (key === 'markets') return path === '/markets' || path === '/' || path.startsWith('/markets/');
    if (key === 'dashboard') return path === '/dashboard';
    return false;
  };

  const tabs = [
    {
      key: 'markets',
      label: 'Markets',
      action: () => { setMoreOpen(false); navigate('/markets'); },
      icon: (active) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? T.accent : T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <rect x="3" y="3" width="7" height="7" rx="1"/>
          <rect x="14" y="3" width="7" height="7" rx="1"/>
          <rect x="3" y="14" width="7" height="7" rx="1"/>
          <rect x="14" y="14" width="7" height="7" rx="1"/>
        </svg>
      ),
    },
    {
      key: 'search',
      label: 'Search',
      action: () => { setMoreOpen(false); navigate('/markets'); setTimeout(() => document.querySelector('input[placeholder="Search markets..."]')?.focus(), 200); },
      icon: (active) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? T.accent : T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="11" cy="11" r="7"/>
          <line x1="16.5" y1="16.5" x2="22" y2="22"/>
        </svg>
      ),
    },
    {
      key: 'dashboard',
      label: 'Portfolio',
      action: () => { setMoreOpen(false); navigate(token ? '/dashboard' : '/login'); },
      icon: (active) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? T.accent : T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
        </svg>
      ),
    },
    {
      key: 'more',
      label: 'More',
      action: () => setMoreOpen(o => !o),
      icon: (active) => (
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke={active ? T.accent : T.textMuted} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="3" y1="6" x2="21" y2="6"/>
          <line x1="3" y1="12" x2="21" y2="12"/>
          <line x1="3" y1="18" x2="21" y2="18"/>
        </svg>
      ),
    },
  ];

const moreItems = [
    { label: 'Profile',     icon: '👤', action: () => { navigate('/profile');     setMoreOpen(false); } },
    { label: 'Chama',       icon: '🤝', action: () => { navigate('/chama');       setMoreOpen(false); } },
    { label: 'Leaderboard', icon: '🏆', action: () => { navigate('/leaderboard'); setMoreOpen(false); } },
    { label: 'Deposit',     icon: '💰', action: () => { navigate('/deposit');     setMoreOpen(false); } },
    { label: 'Withdraw',    icon: '💸', action: () => { navigate('/withdraw');    setMoreOpen(false); } },
    { label: 'Terms',       icon: '📄', action: () => { navigate('/terms');       setMoreOpen(false); } },
    { label: 'Responsible Gambling', icon: '⚠️', action: () => { navigate('/responsible-gambling'); setMoreOpen(false); } },
    token
      ? { label: 'Sign Out', icon: '🚪', danger: true, action: async () => { await logout?.(); navigate('/markets'); setMoreOpen(false); }}
      : { label: 'Sign In',  icon: '🔑', action: () => { navigate('/login'); setMoreOpen(false); } },
  ];

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div onClick={() => setMoreOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 299 }} />
      )}

      {/* More sheet */}
      {moreOpen && (
        <div style={{ position: 'fixed', bottom: 60, left: 0, right: 0, zIndex: 300, background: T.surface, borderTop: `1px solid ${T.border}`, borderRadius: '16px 16px 0 0', paddingBottom: 8, animation: 'slideUp 0.2s ease' }}>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '10px 0 14px' }}>
            <div style={{ width: 32, height: 4, borderRadius: 2, background: T.border }} />
          </div>
          {moreItems.map((item, i) => (
            <button key={i} onClick={item.action}
              style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, padding: '14px 24px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 15, fontWeight: 500, color: item.danger ? '#ea3943' : T.text, fontFamily: "'DM Sans',sans-serif", textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = T.surfaceHi}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}>
              <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{item.icon}</span>
              {item.label}
            </button>
          ))}
        </div>
      )}

      {/* Nav bar */}
      <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 298, background: T.surface, borderTop: `1px solid ${T.border}`, display: 'flex', paddingBottom: 'env(safe-area-inset-bottom)' }}>
        {tabs.map(tab => {
          const active = isActive(tab.key);
          return (
            <button key={tab.key} onClick={tab.action}
              style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '10px 0 8px', background: 'none', border: 'none', cursor: 'pointer' }}>
              {tab.icon(active)}
              <span style={{ fontSize: 10, fontWeight: active ? 700 : 400, color: active ? T.accent : T.textMuted, fontFamily: "'DM Sans',sans-serif", letterSpacing: '0.02em' }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>
    </>
  );
}